import { trpc } from "~/lib/trpc"
import type { Coach } from "../../../types/coach-os.types"
import { Button } from "@aix/ui"
import { useState } from "react"

interface PlanHistoryTabProps {
  coach: Coach
}

export default function PlanHistoryTab({ coach }: PlanHistoryTabProps) {
  const [expandedPlanId, setExpandedPlanId] = useState<string | null>(null)

  // Get all quarterly plans (active + archived)
  const { data: plansData, isLoading } = trpc.plans.quarterlyPlans.list.useQuery({
    coachId: coach.id,
    limit: 50,
  })

  const plans = plansData?.quarterlyPlans || []
  const activePlans = plans.filter((p) => p.status === "active")
  const archivedPlans = plans.filter((p) => p.status === "archived")

  if (isLoading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map((i) => (
          <div key={i} className="bg-secondary border border-border rounded-lg p-6 animate-pulse">
            <div className="h-6 bg-muted rounded w-1/3 mb-3"></div>
            <div className="h-4 bg-muted rounded w-2/3 mb-2"></div>
            <div className="h-4 bg-muted rounded w-1/2"></div>
          </div>
        ))}
      </div>
    )
  }

  if (plans.length === 0) {
    return (
      <div className="bg-secondary border border-border rounded-lg p-12 text-center">
        <div className="text-6xl mb-4">📋</div>
        <h3 className="text-xl font-semibold text-foreground mb-2">No Plans Yet</h3>
        <p className="text-muted-foreground">
          Complete your intake conversation to generate your first quarterly plan.
        </p>
      </div>
    )
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    })
  }

  const getStatusBadge = (status: string) => {
    const styles = {
      active: "bg-primary/20 text-primary border-primary/30",
      archived: "bg-muted text-muted-foreground border-border",
      completed: "bg-green-500/20 text-green-600 border-green-500/30",
      draft: "bg-blue-500/20 text-blue-600 border-blue-500/30",
    }
    return (
      <span
        className={`px-2 py-1 rounded-md text-xs font-medium border capitalize ${
          styles[status as keyof typeof styles] || styles.archived
        }`}
      >
        {status}
      </span>
    )
  }

  const getArchiveReasonText = (metadata: any) => {
    if (!metadata?.archivedReason) return null

    const reasons = {
      "user-reset": "Reset by user",
      completed: "Plan completed",
      abandoned: "Plan abandoned",
      "replaced-by-new-plan": "Replaced by new plan",
    }

    return reasons[metadata.archivedReason as keyof typeof reasons] || metadata.archivedReason
  }

  return (
    <div className="space-y-6">
      {/* Active Plans */}
      {activePlans.length > 0 && (
        <div>
          <h2 className="text-lg font-semibold text-foreground mb-3">Active Plan</h2>
          <div className="space-y-3">
            {activePlans.map((plan) => (
              <div key={plan.id} className="bg-secondary border-2 border-primary/30 rounded-lg p-6">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className="text-xl font-bold text-foreground">{plan.durationWeeks}-Week Plan</h3>
                      {getStatusBadge(plan.status)}
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {formatDate(plan.startDate)} - {formatDate(plan.endDate)}
                    </p>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold text-primary">
                      Week {plan.currentWeek}/{plan.durationWeeks}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      {Math.round((plan.currentWeek / plan.durationWeeks) * 100)}% complete
                    </div>
                  </div>
                </div>

                {/* Progress Bar */}
                <div className="h-2 bg-muted rounded-full overflow-hidden mb-4">
                  <div
                    className="h-full bg-primary transition-all duration-500"
                    style={{
                      width: `${(plan.currentWeek / plan.durationWeeks) * 100}%`,
                    }}
                  />
                </div>

                {/* Goals from Intake Snapshot */}
                {plan.intakeSnapshot?.goals && (
                  <div className="bg-background/50 rounded-lg p-4 mt-4">
                    <h4 className="text-sm font-semibold text-foreground mb-2">Goals:</h4>
                    <ul className="space-y-1">
                      {plan.intakeSnapshot.goals.map((goal: any, idx: number) => (
                        <li key={idx} className="text-sm text-muted-foreground flex items-start">
                          <span className="mr-2">•</span>
                          <span>{typeof goal === "string" ? goal : goal.description}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Phases */}
                {plan.phases && Array.isArray(plan.phases) && plan.phases.length > 0 && (
                  <div className="mt-4">
                    <h4 className="text-sm font-semibold text-foreground mb-2">Phases:</h4>
                    <div className="flex gap-2 flex-wrap">
                      {(plan.phases as any[]).map((phase: any, idx: number) => (
                        <div key={idx} className="bg-background/50 rounded-md px-3 py-1.5 text-xs">
                          <span className="font-medium text-foreground">{phase.name}</span>
                          {phase.weeks && <span className="text-muted-foreground ml-1">(Weeks {phase.weeks})</span>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Archived Plans */}
      {archivedPlans.length > 0 && (
        <div>
          <h2 className="text-lg font-semibold text-foreground mb-3">Plan History ({archivedPlans.length})</h2>
          <div className="space-y-3">
            {archivedPlans.map((plan) => {
              const isExpanded = expandedPlanId === plan.id
              const metadata = plan.metadata
              const archiveReason = getArchiveReasonText(metadata)
              const completedWeeks = metadata?.completedWeeks || 0

              return (
                <div key={plan.id} className="bg-secondary border border-border rounded-lg overflow-hidden">
                  <button
                    onClick={() => setExpandedPlanId(isExpanded ? null : plan.id)}
                    className="w-full p-4 text-left hover:bg-background/30 transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-semibold text-foreground">{plan.durationWeeks}-Week Plan</h3>
                          {getStatusBadge(plan.status)}
                          {archiveReason && <span className="text-xs text-muted-foreground">• {archiveReason}</span>}
                        </div>
                        <p className="text-sm text-muted-foreground">
                          {formatDate(plan.startDate)} - {formatDate(plan.endDate)}
                          {completedWeeks > 0 && (
                            <span className="ml-2">
                              • Completed {completedWeeks}/{plan.durationWeeks} weeks
                            </span>
                          )}
                        </p>
                      </div>
                      <div className="text-muted-foreground ml-4">{isExpanded ? "▼" : "▶"}</div>
                    </div>
                  </button>

                  {/* Expanded Content */}
                  {isExpanded && (
                    <div className="px-4 pb-4 border-t border-border pt-4">
                      {/* Goals from Intake Snapshot */}
                      {plan.intakeSnapshot?.goals && (
                        <div className="mb-4">
                          <h4 className="text-sm font-semibold text-foreground mb-2">Goals:</h4>
                          <ul className="space-y-1">
                            {plan.intakeSnapshot.goals.map((goal: any, idx: number) => (
                              <li key={idx} className="text-sm text-muted-foreground flex items-start">
                                <span className="mr-2">•</span>
                                <span>{typeof goal === "string" ? goal : goal.description}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {/* Phases */}
                      {plan.phases && Array.isArray(plan.phases) && plan.phases.length > 0 && (
                        <div className="mb-4">
                          <h4 className="text-sm font-semibold text-foreground mb-2">Phases:</h4>
                          <div className="flex gap-2 flex-wrap">
                            {(plan.phases as any[]).map((phase: any, idx: number) => (
                              <div key={idx} className="bg-background rounded-md px-3 py-1.5 text-xs">
                                <span className="font-medium text-foreground">{phase.name}</span>
                                {phase.weeks && (
                                  <span className="text-muted-foreground ml-1">(Weeks {phase.weeks})</span>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Metadata */}
                      {metadata && (
                        <div className="bg-background rounded-lg p-3 text-xs space-y-1">
                          {metadata.archivedAt && (
                            <p className="text-muted-foreground">
                              <span className="font-medium">Archived:</span> {formatDate(metadata.archivedAt)}
                            </p>
                          )}
                          {metadata.resetFrom && (
                            <p className="text-muted-foreground">
                              <span className="font-medium">Reset from:</span> {metadata.resetFrom}
                            </p>
                          )}
                          {metadata.previousAttempt && (
                            <p className="text-muted-foreground">
                              <span className="font-medium">Previous attempt:</span>{" "}
                              {metadata.previousAttempt.completedWeeks} weeks completed
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}
